import {
  ErrorCode,
  GamePhase,
  OptionSide,
  WsCommand,
  WsEvent,
  type IAssignment,
  type IRankingEntry,
  type ITurnRevealedPayload,
  type ITurnStartedPayload,
  type IWsSubmitDecisionPayload,
  type IMaintenancePayload,
  type IWsSuggestionPayload,
  type SpiceLevel,
} from "@badorbad/shared";
import { create } from "zustand";
import * as ws from "../lib/ws";
import type { WsMessage } from "../lib/ws";
import { supabase } from "../lib/supabase";
import { t } from "../locale";

export interface Player {
  user_id: string;
  display_name: string;
  avatar_seed: string;
  is_host: boolean;
  connected: boolean;
}

/**
 * Why the client stopped being in a room. Drives the notice shown on the
 * landing screen after the redirect — without it every exit looks identical to
 * "you pressed leave", which is the one case that needs no explanation.
 */
export enum RoomExit {
  Kicked = "kicked",
  Cancelled = "cancelled",
  OpenedElsewhere = "opened_elsewhere",
  /**
   * The room is simply not there any more — the two-hour inactivity TTL lapsed,
   * or it was closed while this client was away. Distinct from `Cancelled`,
   * which is the host closing it in front of you, even though both currently
   * read the same to a player.
   */
  Gone = "gone",
  /**
   * The player pressed Leave. Carries no notice — it is the one exit that needs
   * no explanation — but it still has to be an explicit reason, because
   * "no phase" on its own is indistinguishable from a room that vanished.
   */
  Left = "left",
}

interface JoinPayload extends Record<string, unknown> {
  code: string;
  display_name?: string;
  avatar_seed?: string;
}

/** A suggestion that has arrived, tagged with the half that asked for it. */
export interface DeliveredSuggestion extends IWsSuggestionPayload {
  side: OptionSide;
}

interface GameState {
  phase: GamePhase | null;
  roomCode: string | null;
  players: Player[];
  version: number;
  myUserId: string | null;
  error: string | null;
  exit: RoomExit | null;
  connecting: boolean;
  pendingCode: string | null;
  pendingJoin: JoinPayload | null;
  resumePending: boolean;
  wsReady: boolean;

  /** Setup phase. `assignments` are this player's only — never the room's. */
  assignments: IAssignment[];
  setupDeadlineAt: string | null;
  submittedIndices: number[];
  submittedPlayers: string[];
  /** Delivered suggestions are queued so closely timed responses cannot replace each other. */
  suggestionQueue: DeliveredSuggestion[];
  /** Request id -> destination half. Responses may arrive in any order. */
  pendingSuggestions: Record<string, OptionSide>;

  /**
   * Turn phase. `turn` deliberately has no author — the server withholds who
   * wrote the decision until reveal, and the client must not be able to leak
   * what it was never sent.
   */
  turn: ITurnStartedPayload | null;
  respondedPlayers: string[];
  /** This client's own answer, so the chosen half stays lit before reveal. */
  myResponse: OptionSide | null;
  reveal: ITurnRevealedPayload | null;
  ranking: IRankingEntry[] | null;
  /** When results returns to the lobby on its own. Absolute instant. */
  resultsDeadlineAt: string | null;
  /** Transient, never persisted. Cleared as each one finishes floating. */
  reactions: Array<{ id: number; emoji: string; x: number; y: number }>;

  /**
   * Maintenance is a property of the deployment, not of a room, so it lives
   * outside `emptyRoom()` — leaving a room must not clear it.
   */
  maintenance: IMaintenancePayload | null;

  init: () => Promise<void>;
  setPendingCode: (code: string | null) => void;
  clearExit: () => void;
  setExit: (reason: RoomExit) => void;
  resume: () => Promise<void>;
  join: (displayName: string, avatarSeed: string) => Promise<void>;
  startGame: () => void;
  kick: (userId: string) => void;
  transferHost: (userId: string) => void;
  leave: () => boolean;
  cancelRoom: () => void;
  requestSuggestion: (spice: SpiceLevel, side: OptionSide) => void;
  clearSuggestion: () => void;
  submitDecision: (payload: IWsSubmitDecisionPayload) => void;
  choose: (side: OptionSide) => void;
  guess: (side: OptionSide) => void;
  ensureConnected: () => Promise<void>;
  reconnectAsCurrentUser: () => Promise<void>;
  requestSnapshot: () => void;
  advanceReveal: () => void;
  closeResults: () => void;
  react: (emoji: string) => void;
  dismissReaction: (id: number) => void;
  handleMessage: (msg: WsMessage) => void;
}

/** Everything except the connection itself. Used by every exit path. */
const emptyRoom = (): Pick<
  GameState,
  | "phase"
  | "roomCode"
  | "players"
  | "version"
  | "error"
  | "connecting"
  | "pendingCode"
  | "pendingJoin"
  | "resumePending"
  | "assignments"
  | "setupDeadlineAt"
  | "submittedIndices"
  | "submittedPlayers"
  | "suggestionQueue"
  | "pendingSuggestions"
  | "turn"
  | "respondedPlayers"
  | "myResponse"
  | "reveal"
  | "ranking"
  | "resultsDeadlineAt"
  | "reactions"
> => ({
  phase: null,
  roomCode: null,
  players: [],
  version: 0,
  error: null,
  connecting: false,
  pendingCode: null,
  pendingJoin: null,
  resumePending: false,
  ...emptyGame(),
});

/** Per-game state. Cleared between games as well as on leaving the room. */
const emptyGame = () => ({
  assignments: [] as IAssignment[],
  setupDeadlineAt: null as string | null,
  submittedIndices: [] as number[],
  submittedPlayers: [] as string[],
  suggestionQueue: [] as DeliveredSuggestion[],
  pendingSuggestions: {} as Record<string, OptionSide>,
  turn: null as ITurnStartedPayload | null,
  respondedPlayers: [] as string[],
  myResponse: null as OptionSide | null,
  reveal: null as ITurnRevealedPayload | null,
  ranking: null as IRankingEntry[] | null,
  resultsDeadlineAt: null as string | null,
  reactions: [] as Array<{ id: number; emoji: string; x: number; y: number }>,
});

/**
 * Errors are momentary, but nothing used to clear one: a rate-limited reaction
 * during a reveal was still on screen in the lobby two screens later. Every
 * error now expires on its own, and a newer error restarts the clock rather
 * than being cut short by the older one's timer.
 */
const ERROR_LIFETIME_MS = 5_000;
let errorTimer: ReturnType<typeof setTimeout> | null = null;

const expireError = (
  set: (partial: Partial<GameState>) => void,
  get: () => GameState,
) => {
  if (errorTimer) clearTimeout(errorTimer);
  const shown = get().error;
  errorTimer = setTimeout(() => {
    errorTimer = null;
    // Only clear what we put up. Anything newer owns the slot now.
    if (get().error === shown) set({ error: null });
  }, ERROR_LIFETIME_MS);
};

/**
 * Whether the server restored a room during the current connection.
 *
 * The server sends `RoomState` *before* `Connected` when it has a room for this
 * player, so `Connected` is the point at which "nothing came back" is a fact
 * rather than a race. A dropped socket keeps its stale phase — deliberately, so
 * the screen does not blank on every blip — which means without this the player
 * sits on a dead room screen forever when the room vanished while they were
 * offline.
 */
let restored = false;

/** Reactions are transient and unkeyed on the wire; this is only for React. */
let reactionSeq = 0;
const nextReactionId = () => ++reactionSeq;

/** Identifies which player row is ours — nothing in the room state says so. */
const readUserId = async (set: (partial: Partial<GameState>) => void) => {
  const { data } = await supabase.auth.getSession();
  set({ myUserId: data.session?.user.id ?? null });
};

const patchPlayer = (
  players: Player[],
  userId: string,
  fields: Partial<Player>,
): Player[] =>
  players.map((p) => (p.user_id === userId ? { ...p, ...fields } : p));

export const useGame = create<GameState>((set, get) => ({
  ...emptyRoom(),
  myUserId: null,
  exit: null,
  wsReady: false,
  maintenance: null,

  handleMessage: (msg: WsMessage) => {
    const state = get();
    const userId = (msg.payload.user_id as string) ?? "";

    switch (msg.event) {
      case WsEvent.RoomState:
        restored = true;
        set({
          phase: msg.payload.phase as GamePhase,
          roomCode: msg.payload.code as string,
          players: msg.payload.players as Player[],
          version: msg.version,
          connecting: false,
          resumePending: false,
          wsReady: true,
          error: null,
          exit: null,
        });
        break;

      case WsEvent.PlayerJoined: {
        const joined = msg.payload as unknown as Player;
        // The joiner is subscribed to the room before this is published, so
        // they receive their own arrival on top of the RoomState they were
        // sent directly. Replace rather than append.
        set({
          players: [
            ...state.players.filter((p) => p.user_id !== joined.user_id),
            joined,
          ],
          version: msg.version,
        });
        break;
      }

      case WsEvent.PlayerLeft:
        set({
          players: state.players.filter((p) => p.user_id !== userId),
          version: msg.version,
        });
        break;

      case WsEvent.PlayerKicked:
        if (userId === state.myUserId) {
          ws.disconnect();
          set({ ...emptyRoom(), wsReady: false, exit: RoomExit.Kicked });
        } else {
          set({
            players: state.players.filter((p) => p.user_id !== userId),
            version: msg.version,
          });
        }
        break;

      case WsEvent.HostChanged:
        set({
          players: state.players.map((p) => ({
            ...p,
            is_host: p.user_id === userId,
          })),
          version: msg.version,
        });
        break;

      case WsEvent.PlayerConnected:
        set({
          players: patchPlayer(state.players, userId, { connected: true }),
          version: msg.version,
        });
        break;

      case WsEvent.PlayerDisconnected:
        set({
          players: patchPlayer(state.players, userId, { connected: false }),
          version: msg.version,
        });
        break;

      case WsEvent.RoomCancelled:
        ws.disconnect();
        set({ ...emptyRoom(), wsReady: false, exit: RoomExit.Cancelled });
        break;

      case WsEvent.OpenedElsewhere:
        // The server closes this socket straight after. Stop reconnecting, or
        // the two tabs take the room from each other indefinitely.
        ws.disconnect();
        set({ ...emptyRoom(), wsReady: false, exit: RoomExit.OpenedElsewhere });
        break;

      case WsEvent.GameStarted:
        // GameStarted is broadcast to the room, so it cannot carry assignments
        // — those are per-player. Ask for the personalised SetupState.
        set({
          ...emptyGame(),
          phase: GamePhase.DecisionSetup,
          setupDeadlineAt: msg.payload.deadline_at as string,
          submittedPlayers: msg.payload.submitted_players as string[],
          version: msg.version,
        });
        ws.send(WsCommand.Snapshot);
        break;

      case WsEvent.SetupState:
        set({
          phase: GamePhase.DecisionSetup,
          assignments: msg.payload.assignments as IAssignment[],
          setupDeadlineAt: msg.payload.deadline_at as string,
          submittedPlayers: msg.payload.submitted_players as string[],
          submittedIndices: (msg.payload.submitted_indices as number[]) ?? [],
          version: msg.version,
        });
        break;

      case WsEvent.Suggestion: {
        const payload = msg.payload as unknown as IWsSuggestionPayload;
        const side = state.pendingSuggestions[payload.request_id];
        if (!side) break;
        const pendingSuggestions = { ...state.pendingSuggestions };
        delete pendingSuggestions[payload.request_id];
        set({
          suggestionQueue: [...state.suggestionQueue, { ...payload, side }],
          pendingSuggestions,
        });
        break;
      }

      case WsEvent.DecisionSubmitted: {
        const index = msg.payload.assignment_index as number;
        set({
          submittedIndices: state.submittedIndices.includes(index)
            ? state.submittedIndices
            : [...state.submittedIndices, index],
        });
        break;
      }

      case WsEvent.PlayerSubmitted:
        set({
          submittedPlayers: state.submittedPlayers.includes(userId)
            ? state.submittedPlayers
            : [...state.submittedPlayers, userId],
        });
        break;

      case WsEvent.SetupClosed:
        set({ phase: GamePhase.TurnOrderCreation, version: msg.version });
        break;

      case WsEvent.TurnStarted:
        set({
          phase: GamePhase.TurnActive,
          turn: msg.payload as unknown as ITurnStartedPayload,
          respondedPlayers:
            (msg.payload.responded_players as string[]) ?? [],
          // A reconnect mid-turn replays TurnStarted, and the server does not
          // say which side this client picked — only that it responded. The
          // half stays unlit rather than guessing wrong.
          myResponse: null,
          reveal: null,
          reactions: [],
          version: msg.version,
        });
        break;

      case WsEvent.PlayerResponded:
        set({
          respondedPlayers: state.respondedPlayers.includes(userId)
            ? state.respondedPlayers
            : [...state.respondedPlayers, userId],
        });
        break;

      case WsEvent.TurnRevealed:
        set({
          phase: GamePhase.Reveal,
          reveal: msg.payload as unknown as ITurnRevealedPayload,
          version: msg.version,
        });
        break;

      case WsEvent.GameOver:
        set({
          phase: GamePhase.ResultsFinalized,
          ranking: msg.payload.ranking as IRankingEntry[],
          resultsDeadlineAt: (msg.payload.results_deadline_at as string) ?? null,
          turn: null,
          reveal: null,
          version: msg.version,
        });
        break;

      case WsEvent.Reaction:
        set({
          reactions: [
            ...state.reactions,
            {
              id: nextReactionId(),
              emoji: msg.payload.emoji as string,
              // Fixed here rather than derived in the component: a position
              // computed at render would jump on every re-render, and one
              // derived from the id marched across the screen in a visible
              // cycle instead of scattering.
              x: 8 + Math.random() * 80,
              y: 2 + Math.random() * 30,
            },
          ],
        });
        break;

      case WsEvent.LobbyReturned:
        set({ ...emptyGame(), phase: GamePhase.Lobby, version: msg.version });
        break;

      case WsEvent.Maintenance:
        set({ maintenance: msg.payload as unknown as IMaintenancePayload });
        break;

      case WsEvent.Connected: {
        set({ wsReady: true });
        const pending = get().pendingJoin;
        if (pending) {
          ws.send(WsCommand.Join, pending as Record<string, unknown>);
          set({ pendingJoin: null });
          break;
        }
        // We believed we were in a room and the handshake brought nothing back,
        // so it is gone. Without this the stale phase survives the reconnect
        // and the player keeps staring at a room that no longer exists.
        if (!restored && get().roomCode !== null) {
          set({ ...emptyRoom(), wsReady: true, exit: RoomExit.Gone });
        }
        break;
      }

      case WsEvent.Error:
        if (state.resumePending) {
          const needsName = msg.payload.code === ErrorCode.Validation;
          set({
            resumePending: false,
            connecting: false,
            pendingJoin: null,
            error: needsName ? null : (msg.payload.message as string),
          });
          break;
        }
        set({ error: msg.payload.message as string, connecting: false });
        expireError(set, get);
        break;
    }
  },

  init: async () => {
    await readUserId(set);
    ws.setListener(get().handleMessage);
    // A dropped socket used to leave `wsReady` true, so the lobby kept offering
    // controls whose commands went nowhere.
    ws.setCloseListener(() => {
      restored = false;
      set({ wsReady: false });
    });
    try {
      await ws.connect();
    } catch {
      // No session or offline — nothing to reconnect to.
    }
  },

  setPendingCode: (code) => set({ pendingCode: code }),

  clearExit: () => set({ exit: null }),

  setExit: (reason) => set({ exit: reason }),

  resume: async () => {
    const code = get().pendingCode;
    if (!code) return;

    const payload: JoinPayload = { code };
    set({
      resumePending: true,
      connecting: true,
      pendingJoin: null,
      error: null,
      exit: null,
    });

    try {
      if (!ws.isConnected()) {
        set({ pendingJoin: payload });
        await ws.connect();
      } else if (get().wsReady) {
        ws.send(WsCommand.Join, payload);
      } else {
        set({ pendingJoin: payload });
      }
    } catch {
      set({
        resumePending: false,
        connecting: false,
        error: "Connection failed. Try again.",
        pendingJoin: null,
      });
    }
  },

  join: async (displayName: string, avatarSeed: string) => {
    set({
      connecting: true,
      resumePending: false,
      error: null,
      exit: null,
    });
    // Re-read: init() runs before the landing screen signs the player in, so
    // the id captured at startup is null for everyone arriving as a guest.
    await readUserId(set);

    const code = get().pendingCode ?? "";
    const joinPayload: JoinPayload = {
      code,
      display_name: displayName,
      avatar_seed: avatarSeed,
    };

    try {
      if (!ws.isConnected()) {
        set({ pendingJoin: joinPayload });
        await ws.connect();
      } else if (get().wsReady) {
        ws.send(WsCommand.Join, joinPayload as Record<string, unknown>);
      } else {
        set({ pendingJoin: joinPayload });
      }
    } catch {
      set({ connecting: false, error: "Connection failed. Try again.", pendingJoin: null });
    }
  },

  startGame: () => ws.send(WsCommand.StartGame),

  kick: (userId) => ws.send(WsCommand.Kick, { target_user_id: userId }),

  transferHost: (userId) =>
    ws.send(WsCommand.TransferHost, { target_user_id: userId }),

  cancelRoom: () => ws.send(WsCommand.Cancel),

  requestSuggestion: (spice, side) => {
    const requestId = globalThis.crypto.randomUUID();
    set((state) => ({
      pendingSuggestions: {
        ...state.pendingSuggestions,
        [requestId]: side,
      },
    }));
    if (!ws.send(WsCommand.RequestSuggestion, { request_id: requestId, spice })) {
      set((state) => {
        const pendingSuggestions = { ...state.pendingSuggestions };
        delete pendingSuggestions[requestId];
        return { pendingSuggestions };
      });
    }
  },

  clearSuggestion: () =>
    set((state) => ({ suggestionQueue: state.suggestionQueue.slice(1) })),

  submitDecision: (payload) =>
    ws.send(WsCommand.SubmitDecision, payload as unknown as Record<string, unknown>),

  // Optimistic: the server acknowledges with PlayerResponded, which does not
  // name a side, so the lit half has to come from what we sent. A refusal
  // arrives as Error and the turn is unchanged either way.
  choose: (side) => {
    if (ws.send(WsCommand.Choose, { side })) set({ myResponse: side });
  },

  guess: (side) => {
    if (ws.send(WsCommand.Guess, { side })) set({ myResponse: side });
  },

  /**
   * Opens the socket if it is not already open.
   *
   * `ws.connect()` gives up silently when there is no token yet — which is the
   * normal state at page load, before the anonymous sign-in has happened. It
   * leaves no socket and schedules no retry, so nothing reconnected for the
   * life of the page unless the player happened to join a room. A client that
   * already had a phase would then sit on "Reconnecting..." forever, since the
   * phase survives a drop but the connection was never being attempted.
   */
  ensureConnected: async () => {
    if (ws.isConnected()) return;
    try {
      await ws.connect();
    } catch {
      // The caller retries; a failure here is just this attempt.
    }
  },

  /**
   * Re-opens the socket under whoever is signed in now.
   *
   * The socket authenticates with the token it held at connect, so after a
   * sign-in it is still the previous identity — every command it sent would be
   * attributed to the guest, including a Join. Signing in therefore has to tear
   * the connection down and build it again, not merely update a store field.
   */
  reconnectAsCurrentUser: async () => {
    ws.disconnect();
    set({ ...emptyRoom(), wsReady: false, exit: null });
    await readUserId(set);
    try {
      await ws.connect();
    } catch {
      // Room.tsx retries while it shows the reconnecting state.
    }
  },

  requestSnapshot: () => ws.send(WsCommand.Snapshot),

  advanceReveal: () => ws.send(WsCommand.AdvanceReveal),

  closeResults: () => ws.send(WsCommand.CloseResults),

  react: (emoji) => ws.send(WsCommand.React, { emoji }),

  dismissReaction: (id) =>
    set({ reactions: get().reactions.filter((r) => r.id !== id) }),

  // The socket stays open: the server drops the room binding and the same
  // connection is what creates or joins the next one.
  //
  // Only clear once the command is genuinely on the wire. A dropped Leave would
  // leave the server holding this player in the room while the client believes
  // it left — the reconnect would then haul them back in, and any attempt to
  // create or join another room is refused with "Already in a room".
  leave: () => {
    if (!ws.send(WsCommand.Leave)) {
      set({ error: t.connection.reconnecting });
      return false;
    }
    // Says so explicitly rather than leaving an empty room behind: Room.tsx
    // treats "settled with no phase" as a room that disappeared, and a
    // deliberate leave would otherwise be announced as "this room has closed".
    set({ ...emptyRoom(), exit: RoomExit.Left });
    return true;
  },
}));

/** Assignments this player has not yet locked in, in server order. */
export const selectOpenAssignments = (s: GameState): IAssignment[] =>
  s.assignments.filter((a) => !s.submittedIndices.includes(a.index));

/** The client's own player row, once the room state has arrived. */
export const selectMe = (s: GameState): Player | null =>
  s.players.find((p) => p.user_id === s.myUserId) ?? null;

export const selectIsHost = (s: GameState): boolean =>
  selectMe(s)?.is_host ?? false;
